// Usuarios del panel: alta, rol, bloqueo, reinicio de contraseña y baja.
//
// La contraseña generada se enseña UNA sola vez, con botón de copiar: no se
// guarda en claro en ninguna parte, así que si no se copia hay que reiniciarla.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminAPI } from "../../api/endpoints";
import { Card, ErrorBanner, HelpBox, PageHeader, Spinner } from "../../components/Layout";

const ROLES = ["admin", "analyst", "viewer"] as const;

function Credencial({
  email,
  password,
  onCerrar,
}: {
  email: string;
  password: string;
  onCerrar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles (o sin HTTPS): la contraseña está a la vista
      // y se puede seleccionar a mano, así que no hay nada que rescatar aquí.
      setCopiado(false);
    }
  };

  return (
    <div className="mb-4 p-3 rounded-md bg-estado-ok/10 border border-estado-ok/40">
      <div className="text-sm text-tinta-2 mb-2">
        Contraseña generada para <b className="text-tinta">{email}</b>. Cópiala ahora:{" "}
        <b>no se vuelve a mostrar</b>. Si se pierde, usa «Nueva contraseña» para generar otra.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="font-mono text-sm bg-white px-2 py-1 rounded border border-grid select-all">
          {password}
        </code>
        <button className="btn-sec text-xs" onClick={copiar}>
          {copiado ? "Copiada ✅" : "Copiar"}
        </button>
        <button className="btn-sec text-xs" onClick={onCerrar}>Ya la guardé</button>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["users"], queryFn: AdminAPI.users });

  const [nuevo, setNuevo] = useState({ email: "", nombre: "", rol: "viewer" });
  const [credencial, setCredencial] = useState<{ email: string; password: string } | null>(null);

  const refrescar = () => qc.invalidateQueries({ queryKey: ["users"] });

  const crear = useMutation({
    mutationFn: () => AdminAPI.createUser(nuevo),
    onSuccess: (u) => {
      if (u.password_generada) setCredencial({ email: u.email, password: u.password_generada });
      setNuevo({ email: "", nombre: "", rol: "viewer" });
      refrescar();
    },
  });

  const cambiarRol = useMutation({
    mutationFn: ({ id, rol }: { id: number; rol: string }) => AdminAPI.updateUser(id, { rol }),
    onSuccess: refrescar,
  });

  const alternar = useMutation({
    mutationFn: (id: number) => AdminAPI.toggleUser(id),
    onSuccess: refrescar,
  });

  const reiniciar = useMutation({
    mutationFn: (id: number) => AdminAPI.resetPassword(id),
    onSuccess: (r) => {
      setCredencial({ email: r.email, password: r.password_generada });
      refrescar();
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => AdminAPI.deleteUser(id),
    onSuccess: refrescar,
  });

  // Un solo sitio donde mirar el error de la última acción: ninguna puede fallar
  // en silencio.
  const fallo =
    (crear.error as Error | null) ??
    (cambiarRol.error as Error | null) ??
    (alternar.error as Error | null) ??
    (reiniciar.error as Error | null) ??
    (eliminar.error as Error | null);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader title="Usuarios" subtitle="Crea cuentas, asigna roles y gestiona el acceso." />

      <HelpBox title="Sobre las cuentas y las contraseñas">
        <p>
          Al crear un usuario sin escribir contraseña, el sistema genera una aleatoria y la
          muestra <b>una sola vez</b>: se guarda cifrada, así que nadie —tampoco tú— puede volver
          a leerla. Si se pierde, se genera otra con «Nueva contraseña».
        </p>
        <p>
          Quien entra con una contraseña generada tiene que cambiarla antes de poder usar el
          panel. Ese comportamiento se puede desactivar en Configuración → Seguridad.
        </p>
        <p>
          Los roles: <b>admin</b> lo ve y configura todo; <b>analyst</b> analiza cualquier
          encuesta pero no gestiona el sistema; <b>viewer</b> solo ve lo que se le autorice en
          Permisos.
        </p>
      </HelpBox>

      <Card className="mb-4">
        <h2 className="text-base font-semibold text-tinta mb-3">Nuevo usuario</h2>
        {credencial && (
          <Credencial
            email={credencial.email}
            password={credencial.password}
            onCerrar={() => setCredencial(null)}
          />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="etiqueta">Email</label>
            <input
              type="email"
              className="input"
              value={nuevo.email}
              onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })}
              placeholder="persona@ejemplo.com"
            />
          </div>
          <div>
            <label className="etiqueta">Nombre (opcional)</label>
            <input
              type="text"
              className="input"
              value={nuevo.nombre}
              onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
            />
          </div>
          <div>
            <label className="etiqueta">Rol</label>
            <select
              className="input"
              value={nuevo.rol}
              onChange={(e) => setNuevo({ ...nuevo, rol: e.target.value })}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button
            className="btn"
            disabled={crear.isPending || !nuevo.email.trim()}
            onClick={() => crear.mutate()}
          >
            {crear.isPending ? "Creando…" : "Crear con contraseña generada"}
          </button>
          {crear.isPending && <Spinner />}
        </div>
      </Card>

      {fallo && (
        <div className="mb-4"><ErrorBanner message={fallo.message} /></div>
      )}

      <Card>
        {q.isLoading && <div className="flex items-center gap-2 text-tinta-3"><Spinner /> Cargando…</div>}
        {q.isError && <ErrorBanner message={(q.error as Error).message} />}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-tinta-3 border-b border-grid">
              <tr>
                <th className="py-2 pr-3 text-left">Email</th>
                <th className="py-2 pr-3 text-left">Nombre</th>
                <th className="py-2 pr-3 text-left">Rol</th>
                <th className="py-2 pr-3 text-left">Estado</th>
                <th className="py-2 pr-3 text-left">Último acceso</th>
                <th className="py-2 pr-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.map((u) => (
                <tr key={u.id} className="border-b border-grid">
                  <td className="py-2 pr-3 text-tinta">{u.email}</td>
                  <td className="py-2 pr-3 text-tinta-2">{u.nombre ?? "—"}</td>
                  <td className="py-2 pr-3">
                    <select
                      value={u.role}
                      onChange={(e) => cambiarRol.mutate({ id: u.id, rol: e.target.value })}
                      className="input py-1 text-xs"
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        u.is_active
                          ? "bg-estado-ok/10 text-estado-ok border border-estado-ok/30"
                          : "bg-estado-critico/10 text-estado-critico border border-estado-critico/30"
                      }`}
                    >
                      {u.is_active ? "activo" : "bloqueado"}
                    </span>
                    {u.debe_cambiar_password && (
                      <span
                        className="ml-1 px-2 py-0.5 rounded text-xs bg-estado-warning/15 text-estado-serio border border-estado-warning/40"
                        title="Todavía no ha cambiado la contraseña que se le generó"
                      >
                        pendiente
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-tinta-3 text-xs">
                    {u.last_login ? u.last_login.replace("T", " ").slice(0, 16) : "nunca"}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => alternar.mutate(u.id)}
                      className="text-xs px-2 py-1 rounded border border-grid bg-plano hover:bg-grid text-tinta-2 mr-1"
                    >
                      {u.is_active ? "Bloquear" : "Activar"}
                    </button>
                    <button
                      onClick={() => reiniciar.mutate(u.id)}
                      className="text-xs px-2 py-1 rounded border border-grid bg-plano hover:bg-grid text-tinta-2 mr-1"
                      title="Genera una contraseña nueva y la muestra una vez"
                    >
                      Nueva contraseña
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`¿Eliminar la cuenta de ${u.email}? No se puede deshacer.`)) {
                          eliminar.mutate(u.id);
                        }
                      }}
                      className="text-xs px-2 py-1 rounded border border-estado-critico/40 bg-estado-critico/10 text-estado-critico hover:bg-estado-critico/20"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
